import { useEffect, useRef, MutableRefObject } from 'react';

interface UseAutoFillDatesParams<T> {
    items: T[] | undefined;
    shouldAutoFill: (item: T) => boolean;
    getItemId: (item: T) => number | undefined;
    getValueToFill: (item: T) => string | null | undefined;
    fieldName: string;
    wsHandler: string;
    socketRef: MutableRefObject<any>;
    roomId: string | undefined;
}

/**
 * Custom hook to auto-fill date fields for items without triggering infinite loops.
 * Used for auto-filling startDate in OtherIncome and recoveryStart in Tenants.
 */
export function useAutoFillDates<T>({
    items,
    shouldAutoFill,
    getItemId,
    getValueToFill,
    fieldName,
    wsHandler,
    socketRef,
    roomId,
}: UseAutoFillDatesParams<T>) {
    const autoFilledItemsRef = useRef<Set<number>>(new Set());
    const hasAutoFilledRef = useRef(false);

    useEffect(() => {
        if (!items?.length || hasAutoFilledRef.current || !socketRef?.current || !roomId) {
            return;
        }

        const itemsToUpdate = items.filter(
            item => {
                const itemId = getItemId(item);
                return itemId && shouldAutoFill(item) && !autoFilledItemsRef.current.has(itemId);
            },
        );

        if (itemsToUpdate.length === 0) {
            return;
        }

        // Send all WebSocket updates at once without waiting for responses
        hasAutoFilledRef.current = true;

        itemsToUpdate.forEach(item => {
            const itemId = getItemId(item);
            const valueToFill = getValueToFill(item);

            if (itemId && socketRef.current && valueToFill) {
                autoFilledItemsRef.current.add(itemId);
                // Direct WebSocket emit without creating individual listeners
                socketRef.current.emit(wsHandler, {
                    room: roomId,
                    data: {
                        id: Number(itemId),
                        [fieldName]: valueToFill,
                    },
                });
            }
        });
    }, [items, shouldAutoFill, getItemId, getValueToFill, fieldName, wsHandler, socketRef, roomId]);
}
